import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FadeImage } from '@/components/ui/FadeImage';
import { TwinBio } from '@/components/shared/TwinBio';
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg';
import Link from '@/routing/Link';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfile } from "@/lib/hooks";
import { useTwinChatEnabled } from "@/hooks/useTwinChatEnabled";
import { populationsApi } from "@/lib/api";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, ArrowLeft, Building, ChevronDown, ChevronUp, Lock, MapPin, MessageCircle, Pencil, Plus, Store, Users, X } from "lucide-react";
import { PublisherPill } from '@/components/audiences/PublisherPill';
import { AudienceReadinessNotice } from '@/components/audiences/AudienceReadinessNotice';
import { SegmentProgressIcon } from "@/layouts/AudienceImportSessionLayout/components/SegmentProgressIcon";
import { useSimulatingProgress } from "@/layouts/AudienceImportSessionLayout/hooks/useSimulatingProgress";
import { AudienceTagsDisplay } from "./AudienceTagsDisplay";
import AudienceShareButtons from "./AudienceShareButtons";
import ShareAudienceDialog from "./ShareAudienceDialog";
import RecipientShareDialog from "./RecipientShareDialog";
import { useAudienceSharing } from "../hooks/useAudienceSharing";
import { Button } from "@/components/ui/button";
import type { AudienceSummary } from "@/lib/api";
import type { AudienceTag, NarrativeCardDraft } from "@digital-twins/shared-types";
import {
  resolveAudienceCanonicalCoverUrl,
  resolveAudienceTitleFont,
  resolveAudienceDigitalTwinCount,
} from "../utils/audience-presentation";
import { getDisplayFontConfig, fontSizeStyle, responsiveFontVars } from "../utils/font-config";
import { isUsablePersonaId } from "../utils/isUsablePersonaId";
import { getMatchingTagNames } from "@/lib/utils/tag-matching";
import { getIndustryDisplayName, getIndustryIcon } from "@/lib/constants/industry-categories";
import { audienceTierDisplayName } from "@/lib/billing/display-names";
import { intlLocaleForUi } from "@/i18n";
import { localizedCountryName } from "@/lib/utils/localized-country-name";
import { CREDITS_PER_RESPONDENT, CHAT_CREDITS_PER_MSG, ROOM_CREDITS_PER_MSG } from "@/lib/billing/credit-rates";
import { CreditIcon } from "@/components/icons/CreditIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { canPublishAudienceToMarketplace } from "@/components/audiences/audience-sharing-permissions";
import {
  shouldShowOwnerShareControls,
  shouldShowRecipientShareControls,
} from "../utils/audience-share-routing";


/**
 * Guard against stringified "null"/"undefined" and empty values coming back from the
 * API (e.g. when the backend failed to resolve a persona token for a featured_twin row).
 * We must not build `/twins/<pop>/null` URLs from those.
 */
export function isValidPersonaId(id: unknown): id is string {
  return (
    typeof id === 'string' &&
    id.length > 0 &&
    id !== 'null' &&
    id !== 'undefined'
  );
}

interface PersonaWithImage {
  personaId: string;
  personaName: string;
  attributes: Record<string, string | number | boolean | null>;
  rawAttributes?: Record<string, unknown>;
  normalizedAttributes?: Record<string, unknown>;
  imageUrl?: string;
  resolvedIdentity?: {
    display_name: string;
    age_range: string;
    country: string;
    gender: string;
    ethnicity: string;
  } | null;
  personaNarrative?: string | null;
}

interface FeaturedTwinEntry {
  personaId: string;
  personaName: string;
  imageUrl?: string;
  personaSummary?: string;
  audienceTagNames?: Array<string>;
  profileCard?: { bio?: string; highlights?: Array<string> } | null;
}

interface AudienceDetail extends AudienceSummary {
  featuredTwins?: Array<Record<string, unknown>>;
}

/** Case-insensitive attribute lookup across multiple possible keys */
function findAttr(attrs: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (attrs[key] != null) return attrs[key];
  }
  const lower = new Map(
    Object.entries(attrs).map(([k, v]) => [k.toLowerCase(), v]),
  );
  for (const key of keys) {
    const val = lower.get(key.toLowerCase());
    if (val != null) return val;
  }
  return null;
}

function computeAudienceStats(personas: PersonaWithImage[]) {
  if (personas.length === 0) return null;

  const ages: number[] = [];
  const countries = new Set<string>();
  const jobTitles = new Set<string>();
  const companies = new Set<string>();

  for (const p of personas) {
    const identity = p.resolvedIdentity;
    const attrs = p.attributes ?? {};

    const ageRaw = identity?.age_range ?? findAttr(attrs, "age", "Age");
    if (ageRaw != null) {
      const n =
        typeof ageRaw === "number" ? ageRaw : parseInt(String(ageRaw), 10);
      if (!isNaN(n)) ages.push(n);
    }

    const country =
      identity?.country ??
      findAttr(attrs, "country", "location", "Country", "Location");
    if (country) countries.add(String(country));

    const title = findAttr(
      attrs,
      "job_title",
      "title",
      "role",
      "Job_Title",
      "Title",
      "Role",
    );
    if (title) jobTitles.add(String(title));

    const company = findAttr(
      attrs,
      "company",
      "organization",
      "company_size",
      "Company",
      "Organization",
    );
    if (company) companies.add(String(company));
  }

  return {
    ageMin: ages.length > 0 ? Math.min(...ages) : null,
    ageMax: ages.length > 0 ? Math.max(...ages) : null,
    regions:
      countries.size > 0 ? Array.from(countries).slice(0, 10).join(", ") : null,
    jobTitles:
      jobTitles.size > 0 ? Array.from(jobTitles).slice(0, 10).join(", ") : null,
    companies:
      companies.size > 0 ? Array.from(companies).slice(0, 10).join(", ") : null,
  };
}

const PROFILE_MAX_HEIGHT = 460;

/**
 * AudienceDetailTwinPhoto — the 284x176 (142x88 in the compact edit-preview
 * layout) twin row photo. Its own component so the thumbnail/original
 * fallback state (docs/plans/portrait-thumbnails-adoption.md) is scoped per
 * row instead of being called inside the surrounding `.map()`.
 */
function AudienceDetailTwinPhoto({ imageUrl }: { imageUrl: string }) {
  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 256);

  return (
    <FadeImage
      src={displayUrl ?? imageUrl}
      loading="lazy"
      className="block w-full h-full object-cover"
      style={{ objectPosition: 'center 10%' }}
      onError={() => handleImageError()}
    />
  );
}

function ProfileCard({
  title,
  narrativeProfile,
  stats,
}: {
  title: string;
  narrativeProfile?: Array<{ field: string; value: string }> | null;
  stats: ReturnType<typeof computeAudienceStats>;
}) {
  const { t } = useTranslation('app');
  const [expanded, setExpanded] = useState(false);
  const contentRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) setNeedsExpand(node.scrollHeight > PROFILE_MAX_HEIGHT);
    },
    [],
  );
  const [needsExpand, setNeedsExpand] = useState(false);

  return (
    <div className="bg-muted dark:bg-background rounded-2xl p-7 flex flex-col gap-3 relative">
      <h3 className="text-card-foreground font-bold text-base leading-[1.5]">{title}</h3>
      <div
        ref={contentRef}
        className="flex flex-col gap-3 overflow-hidden transition-[max-height] duration-300"
        style={{ maxHeight: expanded || !needsExpand ? "none" : PROFILE_MAX_HEIGHT }}
      >
        {narrativeProfile && narrativeProfile.length > 0 ? (
          narrativeProfile.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2.5">
              <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-muted-foreground/40" />
              <p className="text-card-foreground text-sm leading-[1.5]">
                <span className="font-bold">{item.field}:</span> {item.value}
              </p>
            </div>
          ))
        ) : (
          <>
            {stats?.regions && (
              <p className="text-card-foreground text-sm leading-[1.5]">
                <span className="font-bold">{t('audiences.regions')}:</span> {stats.regions}
              </p>
            )}
            {stats?.ageMin != null && stats?.ageMax != null && (
              <p className="text-card-foreground text-sm leading-[1.5]">
                <span className="font-bold">{t('audiences.ages')}:</span>{' '}
                {t('audiences.yearsOldRange', { min: stats.ageMin, max: stats.ageMax })}
              </p>
            )}
            {stats?.companies && (
              <p className="text-card-foreground text-sm leading-[1.5]">
                <span className="font-bold">{t('audiences.companySize')}:</span> {stats.companies}
              </p>
            )}
            {stats?.jobTitles && (
              <p className="text-card-foreground text-sm leading-[1.5]">
                <span className="font-bold">{t('audiences.jobTitles')}:</span> {stats.jobTitles}
              </p>
            )}
          </>
        )}
      </div>
      {needsExpand && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 text-primary text-sm hover:underline self-start"
        >
          {expanded ? (
            <>{t('audiences.showLess')} <ChevronUp className="h-3.5 w-3.5" /></>
          ) : (
            <>{t('audiences.showMore')} <ChevronDown className="h-3.5 w-3.5" /></>
          )}
        </button>
      )}
    </div>
  );
}

const editInputClass =
  "w-full bg-background border border-input rounded-lg px-3 py-2 text-foreground text-sm focus:border-primary focus:outline-none transition-colors";

function profileToText(items: Array<{ field: string; value: string }>): string {
  return items.map(item =>
    item.value ? `${item.field} — ${item.value}` : item.field,
  ).join('\n');
}

function textToProfile(text: string): Array<{ field: string; value: string }> {
  return text.split('\n')
    .filter(line => line.trim())
    .map(line => {
      const clean = line.replace(/^[•\-*]\s*/, '').trim();
      const idx = clean.indexOf(' — ');
      if (idx >= 0) return { field: clean.slice(0, idx), value: clean.slice(idx + 3) };
      const idx2 = clean.indexOf('—');
      if (idx2 >= 0) return { field: clean.slice(0, idx2).trim(), value: clean.slice(idx2 + 1).trim() };
      return { field: clean, value: '' };
    });
}

function insightsToText(items: Array<{ title: string; description: string }>): string {
  return items.map(item =>
    item.description ? `${item.title} — ${item.description}` : item.title,
  ).join('\n');
}

function textToInsights(text: string): Array<{ title: string; description: string }> {
  return text.split('\n')
    .filter(line => line.trim())
    .map(line => {
      const clean = line.replace(/^[•\-*]\s*/, '').trim();
      const idx = clean.indexOf(' — ');
      if (idx >= 0) return { title: clean.slice(0, idx), description: clean.slice(idx + 3) };
      const idx2 = clean.indexOf('—');
      if (idx2 >= 0) return { title: clean.slice(0, idx2).trim(), description: clean.slice(idx2 + 1).trim() };
      return { title: clean, description: '' };
    });
}

function NarrativeCardEditor({
  draft,
  onChange,
  onSave,
  onCancel,
  isSaving,
}: {
  draft: NarrativeCardDraft;
  onChange: (d: NarrativeCardDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation('app');
  const set = (field: keyof NarrativeCardDraft, value: unknown) =>
    onChange({ ...draft, [field]: value });

  const [profileText, setProfileText] = useState(() => profileToText(draft.audienceProfile));
  const [insightsText, setInsightsText] = useState(() => insightsToText(draft.topLineInsights));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-10 items-start">
        {/* Left column — text fields */}
        <div className="flex-[3] flex flex-col gap-4 min-w-0">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('audienceActions.header')}</span>
            <input
              value={draft.header}
              onChange={(e) => set("header", e.target.value)}
              className={editInputClass}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('audienceActions.subheader')}</span>
            <textarea
              value={draft.subheader}
              onChange={(e) => set("subheader", e.target.value)}
              rows={2}
              className={`${editInputClass} resize-y`}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('audienceActions.body1')}</span>
            <textarea
              value={draft.bodyParagraph1}
              onChange={(e) => set("bodyParagraph1", e.target.value)}
              rows={4}
              className={`${editInputClass} resize-y`}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('audienceActions.body2')}</span>
            <textarea
              value={draft.bodyParagraph2 ?? ''}
              onChange={(e) => set("bodyParagraph2", e.target.value)}
              rows={4}
              className={`${editInputClass} resize-y`}
            />
          </label>
        </div>

        {/* Right column — profile + insights */}
        <div className="flex-[2] flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('audienceActions.profileFields')}</span>
            <textarea
              value={profileText}
              onChange={(e) => {
                setProfileText(e.target.value);
                set("audienceProfile", textToProfile(e.target.value));
              }}
              rows={8}
              className={`${editInputClass} resize-y`}
              placeholder={t('audienceCreate.narrativeProfilePlaceholder')}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('audienceActions.topInsights')}</span>
            <textarea
              value={insightsText}
              onChange={(e) => {
                setInsightsText(e.target.value);
                set("topLineInsights", textToInsights(e.target.value));
              }}
              rows={8}
              className={`${editInputClass} resize-y`}
              placeholder={t('audienceCreate.narrativeInsightsPlaceholder')}
            />
          </label>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="cta" size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? t('saving', { ns: 'common' }) : t('audienceActions.saveNarrative')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
          className="text-muted-foreground hover:text-foreground"
        >
          {t('cancel', { ns: 'common' })}
        </Button>
      </div>
    </div>
  );
}

function AudienceStatusPlaceholder({
  variant,
  onClose,
  status,
}: {
  variant: "modal" | "page" | "edit-preview";
  onClose?: () => void;
  status: "processing" | "error";
}) {
  const [mountTime] = useState(() => status === 'processing' ? Date.now() : null);
  const simulatingPercent = useSimulatingProgress(status === 'processing', mountTime);
  const { t } = useTranslation('app');

  return (
    <div className="w-full rounded-2xl overflow-hidden bg-card shadow-large">
      {variant === "modal" && (
        <div className="flex justify-end px-4 pt-4">
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-1 px-10 py-16">
        {status === 'processing' ? (
          <>
            <SegmentProgressIcon percent={simulatingPercent} size={82} />
            <h2 className="text-center font-display text-[24px] leading-[1.4] text-card-foreground">
              {t('audienceCreate.buildingAudience')}
            </h2>
            <div className="text-center text-[16px] leading-[1.6] text-muted-foreground max-w-xl">
              <p className="mb-0 whitespace-pre-line">
                {t('audienceCreate.simulatingWait')}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 mb-3">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h2 className="text-center font-display text-[24px] leading-[1.4] text-card-foreground">
              {t('audienceCreate.somethingWrong')}
            </h2>
            <p className="text-center text-[16px] leading-[1.6] text-muted-foreground max-w-md mb-4">
              {t('audiences.buildFailed')}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link href="/audiences">{t('audiences.backToAudiences')}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export interface AudiencePreviewOverrides {
  name?: string;
  tagline?: string;
  coverImageUrl?: string | null;
  titleFont?: string | null;
  audienceType?: string | null;
  respondentCount?: number;
  industryLabels?: Array<{ token: string; name: string; fullPath?: string; tier?: number }> | null;
  narrativeCard?: NarrativeCardDraft | null;
  audienceTags?: Array<AudienceTag> | null;
}

interface AudienceDetailContentProps {
  /** When omitted, the component renders purely from previewOverrides (no API fetches). */
  audienceId?: string | null;
  /** "modal" renders a close X button; "page" renders a chevron link; "edit-preview" hides action buttons */
  variant: "modal" | "page" | "edit-preview";
  onClose?: () => void;
  /** Pre-loaded cover image URL from the list page (used when the detail API doesn't return it) */
  coverImageUrl?: string | null;
  /** When provided, these values override the fetched audience data for live preview */
  previewOverrides?: AudiencePreviewOverrides;
  /** From list row; used when GET omits `canEdit`. Explicit `canEdit: false` from API always wins. */
  initialCanEdit?: boolean;
  /** Whether this viewer can publish/manage marketplace sharing from the shared audience modal. */
  canPublishMarketplace?: boolean;
  /** Revalidate parent audience lists after share state changes that affect cards/visibility. */
  onAudienceChanged?: () => void | Promise<void>;
  /** When provided, "Chat with Twins" opens the compose modal in-page instead of navigating away */
  onOpenChat?: (payload: { audienceId: string; audience?: AudienceSummary | null }) => void;
  /** When provided, per-persona "Chat now" opens the compose modal in-page with the twin preselected */
  onOpenChatWithTwin?: (twin: { personaId: string; populationId: string; personaName?: string; imageUrl?: string }) => void;
  /** Use the admin featured-twins endpoint (bypasses org-token check). Pass true when rendering inside the backoffice. */
  isAdmin?: boolean;
}

export default function AudienceDetailContent({
  audienceId,
  variant,
  onClose,
  coverImageUrl: externalCoverImage,
  previewOverrides,
  initialCanEdit,
  canPublishMarketplace: canPublishMarketplaceProp,
  onAudienceChanged,
  onOpenChat,
  onOpenChatWithTwin,
  isAdmin = false,
}: AudienceDetailContentProps) {
  const { t, i18n } = useTranslation('app');
  const twinChatEnabled = useTwinChatEnabled();
  const navigateTo = useNavigate();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const hasAudienceId = !!audienceId;
  const detailViewTrackedRef = useRef(false);
  const [audience, setAudience] = useState<AudienceDetail | null>(null);
  const [loading, setLoading] = useState(hasAudienceId);
  const [personas, setPersonas] = useState<PersonaWithImage[]>([]);
  const [featuredTwins, setFeaturedTwins] = useState<FeaturedTwinEntry[]>([]);
  const [featuredTwinsResolved, setFeaturedTwinsResolved] = useState(!hasAudienceId);
  const TWINS_PAGE_SIZE = 5;
  const [visibleTwinCount, setVisibleTwinCount] = useState(TWINS_PAGE_SIZE);

  const fetchAudience = useCallback(
    async (silent = false) => {
      if (!audienceId) return null;
      try {
        if (!silent) setLoading(true);
        const data = await populationsApi.getAudienceUiDetail(audienceId);
        setAudience(data);
        return data;
      } catch (error) {
        console.error("Failed to fetch audience:", error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [audienceId],
  );

  const fetchPersonas = useCallback(async () => {
    if (!audienceId) return;
    try {
      const data = await populationsApi.getPersonas(audienceId, {
        page: 1,
        limit: 10,
      });
      setPersonas(data.personas);
    } catch (error) {
      console.error("Failed to fetch personas:", error);
    }
  }, [audienceId]);

  const fetchFeaturedTwins = useCallback(async () => {
    if (!audienceId) {
      setFeaturedTwinsResolved(true);
      return [];
    }
    try {
      const data = isAdmin
        ? await populationsApi.getFeaturedTwinsAdmin(audienceId)
        : await populationsApi.getFeaturedTwins(audienceId);
      const twins = data as FeaturedTwinEntry[];
      setFeaturedTwins(twins);
      return twins;
    } catch {
      // Featured twins endpoint may 404 for some audiences; non-critical
      return [];
    } finally {
      setFeaturedTwinsResolved(true);
    }
  }, [audienceId, isAdmin]);

  useEffect(() => {
    if (!hasAudienceId) return;
    let cancelled = false;

    setFeaturedTwins([]);
    setPersonas([]);
    setFeaturedTwinsResolved(false);
    setVisibleTwinCount(TWINS_PAGE_SIZE);

    async function loadAudienceDetail() {
      const [audienceResult, featuredTwinsResult] = await Promise.allSettled([
        fetchAudience(),
        fetchFeaturedTwins(),
      ]);
      if (cancelled) {
        return;
      }

      const audienceData =
        audienceResult.status === 'fulfilled' ? audienceResult.value : null;
      const twins =
        featuredTwinsResult.status === 'fulfilled'
          ? featuredTwinsResult.value
          : [];
      const needsPersonas = !audienceData?.narrativeCard || twins.length === 0;
      if (needsPersonas) {
        fetchPersonas();
      }
    }

    loadAudienceDetail();
    return () => {
      cancelled = true;
    };
  }, [hasAudienceId, fetchAudience, fetchPersonas, fetchFeaturedTwins]);

  useEffect(() => {
    // Admin preview is read-only — do not fire platform analytics in this context.
    if (!audience?.id || profileLoading || detailViewTrackedRef.current || isAdmin) return;
    detailViewTrackedRef.current = true;

    track(AnalyticsEvents.AUDIENCE_DETAIL_VIEWED, { audience_id: audience.id });

    // Fire the feedback-survey trigger only when the viewer created this audience
    // AND this is the very first time they open it (guarded by a localStorage flag).
    const isCreator =
      profile?.id != null &&
      audience.userId != null &&
      profile.id === audience.userId;
    if (isCreator) {
      const firstViewKey = `dtfg_audience_first_view:${audience.id}`;
      const isFirstView = !localStorage.getItem(firstViewKey);
      if (isFirstView) {
        localStorage.setItem(firstViewKey, '1');
        track(AnalyticsEvents.AUDIENCE_FIRST_CREATOR_VIEW, { audience_id: audience.id });
      }
    }
  }, [audience?.id, audience?.userId, profile?.id, profileLoading]);

  const stats = useMemo(() => computeAudienceStats(personas), [personas]);

  /** API `canEdit` wins when present; otherwise fall back to list row hint (same as after load). */
  const effectiveCanEdit = useMemo(() => {
    return (audience?.canEdit ?? initialCanEdit) === true;
  }, [audience, initialCanEdit]);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [recipientShareDialogOpen, setRecipientShareDialogOpen] = useState(false);
  const canPublishMarketplace = isAdmin ? false : (canPublishMarketplaceProp ?? canPublishAudienceToMarketplace(profile));

  /** RND-2640: server `canEdit` flag — true when the viewer's org has access (owning org or partner org with a grant/invite), or platform admin. */
  const mayEditAudienceAsViewer = useMemo(() => {
    if (!effectiveCanEdit) {
      return false;
    }
    if (profileLoading) {
      return false;
    }
    return true;
  }, [
    effectiveCanEdit,
    profileLoading,
  ]);

  const isEditPreview = variant === "edit-preview";

  const showOwnerShare = shouldShowOwnerShareControls({
    mayEdit: !isAdmin && !isEditPreview && !!audienceId && mayEditAudienceAsViewer,
    canManageOrgSharing: audience?.canManageOrgSharing,
    audienceOrganizationToken: audience?.organizationToken,
    viewerOrganizationToken: profile?.organizationToken,
  });
  const showRecipientShare = !isAdmin && !isEditPreview && !!audienceId && shouldShowRecipientShareControls({
    canManageOrgSharing: audience?.canManageOrgSharing,
  });

  // Admin preview must be read-only — do not init the sharing hook or publish
  // flag for operators/platform admins viewing via backoffice (RND-2638 Phase 4).
  // Partner invitees use RecipientShareDialog, not owner sharingMode.
  const sharing = useAudienceSharing((!isAdmin && showOwnerShare) ? (audienceId ?? null) : null);

  const heroHeight = isEditPreview ? "h-[320px]" : "h-[480px]";
  const resolvedTitleFont =
    previewOverrides?.titleFont ?? resolveAudienceTitleFont(audience);
  const titleFontFamily = resolvedTitleFont
    ? `'${resolvedTitleFont}', sans-serif`
    : undefined;
  const fontConfig = getDisplayFontConfig(resolvedTitleFont);

  const narrativeCard = isEditPreview
    ? (previewOverrides?.narrativeCard ?? null)
    : (audience?.narrativeCard ?? null);

  const [isEditingNarrative, setIsEditingNarrative] = useState(false);
  const [editDraft, setEditDraft] = useState<NarrativeCardDraft | null>(null);
  const [isSavingNarrative, setIsSavingNarrative] = useState(false);

  const startEditingNarrative = useCallback(() => {
    if (!narrativeCard) return;
    setEditDraft({
      header: narrativeCard.header,
      subheader: narrativeCard.subheader,
      bodyParagraph1: narrativeCard.bodyParagraph1,
      bodyParagraph2: narrativeCard.bodyParagraph2 ?? "",
      audienceProfile: narrativeCard.audienceProfile.map((p) => ({ ...p })),
      topLineInsights: narrativeCard.topLineInsights.map((i) => ({ ...i })),
      meta: narrativeCard.meta,
    });
    setIsEditingNarrative(true);
  }, [narrativeCard]);

  const cancelEditingNarrative = useCallback(() => {
    setIsEditingNarrative(false);
    setEditDraft(null);
  }, []);

  const saveNarrative = useCallback(async () => {
    if (!audienceId || !editDraft || !narrativeCard) return;
    setIsSavingNarrative(true);
    try {
      await populationsApi.update(audienceId, {
        narrativeCard: {
          ...narrativeCard,
          header: editDraft.header,
          subheader: editDraft.subheader,
          bodyParagraph1: editDraft.bodyParagraph1,
          bodyParagraph2: editDraft.bodyParagraph2 || null,
          audienceProfile: editDraft.audienceProfile.filter(
            (p) => p.field.trim() && p.value.trim(),
          ),
          topLineInsights: editDraft.topLineInsights.filter(
            (i) => i.title.trim() && i.description.trim(),
          ),
        },
      });
      await fetchAudience(true);
      track(AnalyticsEvents.AUDIENCE_NARRATIVE_SAVED, { audience_id: audienceId });
      setIsEditingNarrative(false);
      setEditDraft(null);
    } catch (err) {
      console.error("Failed to save narrative card:", err);
    } finally {
      setIsSavingNarrative(false);
    }
  }, [audienceId, editDraft, narrativeCard, fetchAudience]);

  const displayName = previewOverrides?.name ?? audience?.name;
  const displayCoverImage =
    previewOverrides?.coverImageUrl ??
    resolveAudienceCanonicalCoverUrl(audience) ??
    externalCoverImage;
  const effectiveAudienceType =
    previewOverrides?.audienceType ?? audience?.audienceType;
  const effectiveRowCount =
    previewOverrides?.respondentCount ??
    resolveAudienceDigitalTwinCount(audience ?? undefined);
  const displayIndustryLabels =
    previewOverrides?.industryLabels ??
    audience?.industryLabels ??
    (audience?.industryLabel ? [audience.industryLabel] : []);

  const effectiveAudienceTags =
    previewOverrides?.audienceTags ?? audience?.audienceTags ?? null;

  const tierLabel = audience?.tier
    ? audienceTierDisplayName(audience.tier)
    : t('billing.audienceTiers.standard');

  const resolvedCountryNames: Array<string> = useMemo(() => {
    const locale = intlLocaleForUi(i18n.language)
    const countryLabels = (audience?.audienceLabels ?? [])
      .filter((l) => l.tier === 6 && l.fullPath.includes('Country Extension'))
      .map((l) => l.name)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    if (countryLabels.length > 0) return countryLabels.map((code) => localizedCountryName(code, locale));
    const raw = audience?.constantContext?.['std_geo_country']?.trim();
    if (!raw) return [];
    return [raw.length === 2 ? localizedCountryName(raw, locale) : raw];
  }, [audience?.audienceLabels, audience?.constantContext, i18n.language]);

  const primaryPopulationIdForChat = audience?.id ?? audienceId;
  const firstMeetTheAudiencePersonaId = useMemo(() => {
    const fromFeatured = featuredTwins.find((t) =>
      isValidPersonaId(t?.personaId),
    )?.personaId;
    if (isValidPersonaId(fromFeatured)) {
      return fromFeatured;
    }
    const fromPersonas = personas.find((p) => isValidPersonaId(p?.personaId))
      ?.personaId;
    return isValidPersonaId(fromPersonas) ? fromPersonas : null;
  }, [featuredTwins, personas]);
  const navigateToChatWithTwin = useCallback(
    (personaId: string, personaName?: string, imageUrl?: string) => {
      navigateTo('/chats?new=1', {
        state: {
          initialTwin: {
            personaId,
            populationId: primaryPopulationIdForChat,
            personaName,
            imageUrl,
          },
        },
      });
    },
    [navigateTo, primaryPopulationIdForChat],
  );

  const aboutText = narrativeCard
    ? null
    : (audience?.aiDescription || audience?.contextText || audience?.description ||
      audience?.stageMetadata?.description);

  const primaryDisplayIndustry = displayIndustryLabels[0]?.name ?? null;
  const industryTagsForHero = primaryDisplayIndustry
    ? displayIndustryLabels.map((l) => ({
        key: l.token,
        name: getIndustryDisplayName(l.name),
        icon: getIndustryIcon(l.fullPath?.split(' | ')[0] ?? l.name),
      }))
    : (audience?.contentLabels?.slice(0, 2).map((l) => ({
        key: l.token,
        name: getIndustryDisplayName(l.name),
        icon: getIndustryIcon(l.name),
      })) ?? []);

  if (loading) {
    return (
      <div className={variant === "page" ? "w-full overflow-hidden" : "w-full rounded-2xl overflow-hidden shadow-large"}>
        <Skeleton className="h-[480px] w-full" />
        <div className="p-[60px] space-y-8">
          <div className="flex gap-7">
            <div className="flex-1 space-y-4">
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
            </div>
            <Skeleton className="h-[280px] w-[320px] rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (audience?.status === 'processing') {
    return (
      <AudienceStatusPlaceholder
        variant={variant}
        onClose={onClose}
        status="processing"
      />
    );
  }

  if (audience?.status === 'error') {
    return (
      <AudienceStatusPlaceholder
        variant={variant}
        onClose={onClose}
        status="error"
      />
    );
  }

  if (!audience && !previewOverrides) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <p className="text-muted-foreground text-lg">Audience not found</p>
        <Link
          href="/audiences"
          className="text-primary hover:underline text-sm"
        >
          Back to Audiences
        </Link>
      </div>
    );
  }

  const heroSubtitle = previewOverrides?.tagline
    ?? narrativeCard?.header
    ?? (() => {
      const fallback = audience?.aiDescription || audience?.stageMetadata?.shortDescription;
      return fallback ? fallback.split(/[.!?]/)[0] : null;
    })();

  return (
    <div
      className={variant === "page" ? "w-full overflow-hidden relative" : "w-full rounded-2xl overflow-hidden relative bg-card shadow-large"}
    >
      {/* Top bar (close/back) — hidden in edit-preview */}
      {!isEditPreview && (
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 sm:px-8 md:px-[60px] py-4">
          {/* Creator badge / publisher pill */}
          {audience?.sharingMode === 'marketplace' ? (
            <PublisherPill
              icon={audience.logoUrl ?? null}
              name={audience.organizationName || audience.publisherDomain || t('audiences.unknown')}
            />
          ) : (
            <CreatorBadge audience={audience} viewerId={profile?.id} />
          )}
          {variant === "modal" ? (
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/50 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          ) : !isAdmin ? (
            <Link
              href="/audiences"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
          ) : null}
        </div>
      )}

      {/* Hero Banner */}
      <div className={`relative w-full ${heroHeight} overflow-hidden`}>
        <div
          className="absolute inset-0"
          style={{ background: 'var(--home-card-placeholder-gradient)' }}
        />
        {displayCoverImage && (
          <FadeImage
            src={displayCoverImage}
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, transparent 41%, hsl(var(--background) / 0.68) 72%, hsl(var(--background)) 100%)",
          }}
        />

        {audience?.publisherDomain && audience.sharingMode !== 'marketplace' && (
          <div className="absolute top-4 left-[60px] z-20">
            <PublisherPill
              icon={audience.logoUrl ?? null}
              name={audience.publisherDomain}
            />
          </div>
        )}

        {/* Bottom: title + tag pills + tagline */}
        <div className={`absolute inset-0 flex flex-col justify-end ${isEditPreview ? "px-6 py-5" : "px-4 sm:px-8 md:px-[60px] pb-8"} z-10`}>
          <div className="flex flex-col gap-1">
            <h1
              className="font-display-anton text-foreground dark:text-white tracking-tight text-[length:var(--detail-hero-fs-sm)] sm:text-[length:var(--detail-hero-fs)]"
              style={isEditPreview
                ? fontSizeStyle(fontConfig.heroEditPreview, titleFontFamily)
                : responsiveFontVars(fontConfig.detailHero, 'detail-hero', titleFontFamily)
              }
            >
              {displayName}
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mt-1">
              <div className="flex flex-col gap-1 min-w-0">
                {heroSubtitle && (
                  <div className="flex items-center gap-3">
                    <p className={`text-foreground dark:text-white font-medium ${isEditPreview ? "text-lg" : "text-[20px]"} leading-[1.4]`}>
                      {heroSubtitle}
                    </p>
                    {false && !isEditPreview && <AudienceShareButtons />}
                  </div>
                )}
              </div>
              {!isAdmin && !isEditPreview && audienceId && (mayEditAudienceAsViewer || showRecipientShare) && (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  {showOwnerShare && (
                    <Button
                      variant="primary"
                      className="shrink-0 gap-2 px-6 py-2.5 text-base"
                      onClick={() => setShareDialogOpen(true)}
                      data-testid="owner-share-button"
                    >
                      <Users className="h-4 w-4" />
                      {t('audiences.share')}
                    </Button>
                  )}
                  {showRecipientShare && (
                    <Button
                      variant="outline"
                      className="shrink-0 gap-2 px-6 py-2.5 text-base"
                      onClick={() => setRecipientShareDialogOpen(true)}
                      data-testid="recipient-share-button"
                    >
                      <Users className="h-4 w-4" />
                      {t('audiences.shareWithOrg')}
                    </Button>
                  )}
                  {mayEditAudienceAsViewer && (
                    <Button asChild variant="outline" className="shrink-0 gap-2 px-6 py-2.5 text-base border-0">
                      <Link href={`/audiences/${audienceId}/edit-stage`}>
                        <Pencil className="h-4 w-4" />
                        {t('questionnaire.edit')}
                      </Link>
                    </Button>
                  )}
                </div>
              )}

              {/* Share dialog — mounted outside the owner-only guard so it can
                  animate out smoothly; rendered only when audienceId is present */}
              {!isAdmin && audienceId && showOwnerShare && (
                <ShareAudienceDialog
                  open={shareDialogOpen}
                  onOpenChange={setShareDialogOpen}
                  audienceId={audienceId}
                  audienceName={displayName ?? ''}
                  sharing={sharing}
                  canPublishMarketplace={canPublishMarketplace}
                  onAudienceChanged={onAudienceChanged}
                  initialSharedAs={audience?.sharedAs ?? 'member'}
                  initialSharedAsUserId={audience?.sharedAsUserId ?? null}
                  isOwner={effectiveCanEdit && !!profile?.id && audience?.userId === profile.id}
                />
              )}
              {showRecipientShare && audienceId && (
                <RecipientShareDialog
                  open={recipientShareDialogOpen}
                  onOpenChange={setRecipientShareDialogOpen}
                  audienceId={audienceId}
                  audienceName={displayName ?? ''}
                  currentScope={audience?.orgGrantScope ?? 'org'}
                />
              )}
            </div>
            {/* Tag pills — below subtitle, same style as cards but bigger */}
            <TooltipProvider delayDuration={0}>
              <div className={`flex flex-wrap items-center ${isEditPreview ? "gap-1.5 mt-2" : "gap-2 mt-3"}`}>
                {industryTagsForHero.map((tag) => {
                  const Icon = tag.icon;
                  return (
                      <span
                        key={tag.key}
                        className={`inline-flex items-center gap-1.5 rounded-full bg-foreground/10 dark:bg-white/15 backdrop-blur-sm font-medium text-foreground/90 dark:text-white/90 ${isEditPreview ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm"}`}
                    >
                      <Icon className={isEditPreview ? "h-3 w-3" : "h-3.5 w-3.5"} />
                      {tag.name}
                    </span>
                  );
                })}
                {resolvedCountryNames.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`inline-flex items-center gap-1.5 rounded-full bg-foreground/10 dark:bg-white/15 backdrop-blur-sm font-medium text-foreground/90 dark:text-white/90 cursor-default ${isEditPreview ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm"}`}>
                        <MapPin className={isEditPreview ? "h-3 w-3" : "h-3.5 w-3.5"} />
                        {resolvedCountryNames[0]}
                        {resolvedCountryNames.length > 1 && (
                          <span className={`inline-flex items-center rounded-full bg-foreground/10 dark:bg-white/20 font-semibold text-foreground/90 dark:text-white/90 ${isEditPreview ? "px-1 py-px text-[10px]" : "px-1.5 py-px text-xs"}`}>
                            +{resolvedCountryNames.length - 1}
                          </span>
                        )}
                      </span>
                    </TooltipTrigger>
                    {resolvedCountryNames.length > 1 && (
                      <TooltipContent
                        side="top"
                        className="z-[9999] max-w-xs text-xs bg-tooltip text-tooltip-foreground shadow-lg border border-border dark:border-transparent"
                      >
                        {resolvedCountryNames.join(', ')}
                      </TooltipContent>
                    )}
                  </Tooltip>
                )}
                {effectiveAudienceType && (
                  <span className={`inline-flex items-center rounded-full bg-foreground/10 dark:bg-white/15 backdrop-blur-sm font-medium text-foreground/90 dark:text-white/90 ${isEditPreview ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm"}`}>
                    {effectiveAudienceType}
                  </span>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={`inline-flex items-center gap-1.5 rounded-full bg-foreground/10 dark:bg-white/15 backdrop-blur-sm font-medium text-foreground/90 dark:text-white/90 cursor-default ${isEditPreview ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm"}`}>
                      <CreditIcon className={isEditPreview ? "h-3 w-3" : "h-3.5 w-3.5"} />
                      {tierLabel}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="p-3 bg-popover text-popover-foreground">
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-muted-foreground">{t('audiences.creditStudy')}</span>
                        <span className="font-medium flex items-center gap-1"><CreditIcon className="h-3 w-3" />{audience?.creditsPerRespondent ?? CREDITS_PER_RESPONDENT[audience?.tier ?? 'tier1'] ?? 1}</span>
                      </div>
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-muted-foreground">{t('audiences.creditInterview')}</span>
                        <span className="font-medium flex items-center gap-1"><CreditIcon className="h-3 w-3" />{audience?.chatCreditsPerMessage ?? CHAT_CREDITS_PER_MSG[audience?.tier ?? 'tier1'] ?? 1}{t('audiences.perMsg')}</span>
                      </div>
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-muted-foreground">{t('audiences.creditFocusGroup')}</span>
                        <span className="font-medium flex items-center gap-1"><CreditIcon className="h-3 w-3" />{audience?.focusGroupCreditsPerMessage ?? ROOM_CREDITS_PER_MSG[audience?.tier ?? 'tier1'] ?? 2}{t('audiences.perMsg')}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
                {effectiveRowCount != null && (
                  <span className={`${isEditPreview ? "text-xs" : "text-sm"} text-foreground/50 dark:text-white/50 font-normal`}>
                    {t('audiences.digitalTwins', {
                      count: effectiveRowCount,
                      formatted: effectiveRowCount.toLocaleString(intlLocaleForUi(i18n.language)),
                    })}
                  </span>
                )}
              </div>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className={`flex flex-col bg-white dark:bg-card ${isEditPreview ? "gap-8 px-6 pt-6 pb-6" : "gap-10 md:gap-[80px] px-4 sm:px-8 md:px-[60px] pt-8 md:pt-12 pb-8 md:pb-[60px]"}`}>
        <AudienceReadinessNotice readiness={audience?.readiness} />
        {/* Two-column: Description + Audience Data */}
        {isEditingNarrative && editDraft ? (
          <NarrativeCardEditor
            draft={editDraft}
            onChange={setEditDraft}
            onSave={saveNarrative}
            onCancel={cancelEditingNarrative}
            isSaving={isSavingNarrative}
          />
        ) : (
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start">
            <div className={isEditPreview ? "w-full flex flex-col gap-6 min-w-0" : "flex-[3] flex flex-col gap-6 min-w-0 w-full"}>
              {narrativeCard ? (
                <>
                  {narrativeCard.subheader && (
                    <h2 className={`font-display leading-[1.25] tracking-[-0.02em] text-card-foreground ${isEditPreview ? 'text-[20px]' : 'text-[28px]'}`}>
                      {narrativeCard.subheader}
                    </h2>
                  )}
                  <p className={`text-card-foreground leading-[1.7] whitespace-pre-wrap ${isEditPreview ? 'text-sm' : 'text-base'}`}>
                    {narrativeCard.bodyParagraph1}
                  </p>
                  {narrativeCard.bodyParagraph2 && (
                    <p className={`text-card-foreground leading-[1.7] whitespace-pre-wrap ${isEditPreview ? 'text-sm' : 'text-base'}`}>
                      {narrativeCard.bodyParagraph2}
                    </p>
                  )}
                </>
              ) : (
                aboutText && (
                  <p className={`text-card-foreground leading-[1.7] whitespace-pre-wrap ${isEditPreview ? 'text-sm' : 'text-base'}`}>
                    {aboutText}
                  </p>
                )
              )}

              {/* Admin preview is read-only: AudienceTagsDisplay expansion POSTs to
                  /segment-description (server-side generation), so it is hidden when
                  isAdmin to keep the backoffice preview side-effect-free (RND-2638). */}
              {!isAdmin && effectiveAudienceTags && effectiveAudienceTags.length > 0 && audienceId && (
                <div className="pt-2">
                  <AudienceTagsDisplay tags={effectiveAudienceTags} audienceId={audienceId} />
                </div>
              )}
            </div>

            {!isEditPreview && (
              <div className="flex-[2] flex flex-col gap-5">
                <ProfileCard
                  title={narrativeCard ? t('audiences.audienceProfile') : t('audiences.audienceData')}
                  narrativeProfile={narrativeCard?.audienceProfile}
                  stats={stats}
                />

                {narrativeCard?.topLineInsights && narrativeCard.topLineInsights.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <h3 className="text-card-foreground font-bold text-base leading-[1.5]">{t('audiences.keyInsights')}</h3>
                    <div className="flex flex-col gap-2">
                      {narrativeCard.topLineInsights.map((insight, idx) => (
                        <div key={idx} className="rounded-xl bg-primary/10 px-4 py-3">
                          <p className="text-card-foreground text-base leading-[1.6]">
                            {insight.title && (
                              <strong className="font-semibold">{insight.title}{insight.description ? ' ' : ''}</strong>
                            )}
                            {insight.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Meet the audience — featured twins (preferred) or persona fallback */}
        {(featuredTwins.length > 0 || (featuredTwinsResolved && personas.length > 0)) && (
          <div className="flex flex-col gap-6">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-[24px] sm:text-[32px] leading-[1.2] tracking-[-0.02em] text-card-foreground">
                {t('audiences.meetTheAudience')}
              </h2>
              {effectiveRowCount != null && (
                <span className="text-muted-foreground text-sm shrink-0">
                  {t('audiences.digitalTwins', {
                    count: effectiveRowCount,
                    formatted: effectiveRowCount.toLocaleString(intlLocaleForUi(i18n.language)),
                  })}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              {featuredTwins.length > 0 || !featuredTwinsResolved
                ? featuredTwins.slice(0, visibleTwinCount).map((twin, idx) => {
                    const name = twin.personaName || t('audiences.twinFallback', { n: idx + 1 });

                    return (
                      <div key={isUsablePersonaId(twin.personaId) ? twin.personaId : `twin-${idx}`}>
                        <div className="h-px bg-border" />
                        <div className={`flex flex-col sm:flex-row items-start ${isEditPreview ? "gap-3 sm:gap-4 py-3" : "gap-5 sm:gap-8 py-6"}`}>
                          <div className={`flex flex-col sm:flex-row items-start ${isEditPreview ? "gap-3 sm:gap-4" : "gap-4 sm:gap-5"} flex-1 min-w-0`}>
                            <div className={`${isEditPreview ? "w-full sm:w-[142px] sm:max-w-[142px] h-[88px]" : "w-full sm:w-[284px] sm:max-w-[284px] h-44"} sm:shrink-0 rounded-xl bg-surface overflow-hidden flex items-center justify-center`}>
                              {twin.imageUrl ? (
                                <AudienceDetailTwinPhoto imageUrl={twin.imageUrl} />
                              ) : (
                                <Users className="h-7 w-7 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex flex-col gap-1 min-w-0 max-w-[666px]">
                              <p
                                className={isEditPreview ? "font-display font-normal text-[16px] leading-[1.3] text-card-foreground" : "font-display text-card-foreground"}
                                style={isEditPreview ? undefined : fontSizeStyle(fontConfig.persona, titleFontFamily)}
                              >
                                {String(name)}
                              </p>
                              <TwinBio
                                profileCard={twin.profileCard}
                                fallbackBio={twin.personaSummary}
                                introClassName="text-card-foreground"
                                highlightClassName="text-muted-foreground"
                                fallbackClamp={3}
                                collapsible
                              />
                              {twin.audienceTagNames && twin.audienceTagNames.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {twin.audienceTagNames.map((tn) => (
                                    <span key={tn} className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                                      {tn}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {!isAdmin && twinChatEnabled && isUsablePersonaId(twin.personaId) ? (
                            <Button
                              variant="cta"
                              size="sm"
                              className="shrink-0 gap-2"
                              onClick={() => {
                                if (onOpenChatWithTwin && primaryPopulationIdForChat) {
                                  onOpenChatWithTwin({ personaId: twin.personaId, populationId: primaryPopulationIdForChat, personaName: twin.personaName, imageUrl: twin.imageUrl });
                                } else {
                                  navigateToChatWithTwin(twin.personaId, twin.personaName, twin.imageUrl);
                                }
                              }}
                            >
                              <MessageCircle className="h-4 w-4" />
                              {t('audiences.chatNow')}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                :                   personas.slice(0, visibleTwinCount).map((persona, idx) => {
                    const identity = persona.resolvedIdentity;
                    const attrs = persona.attributes ?? {};

                    const name =
                      persona.personaName ||
                      identity?.display_name ||
                      attrs.name ||
                      attrs.full_name ||
                      t('audiences.twinFallback', { n: idx + 1 });
                    const summary =
                      persona.personaNarrative ||
                      attrs.persona_summary ||
                      attrs.description ||
                      attrs.Description ||
                      attrs.bio ||
                      attrs.Bio;
                    const personaTagNames: string[] = (persona as any).audienceTagNames ?? getMatchingTagNames(
                      { ...attrs, ...((persona.normalizedAttributes && Object.keys(persona.normalizedAttributes).length > 0) ? persona.normalizedAttributes : {}) } as Record<string, unknown>,
                      audience?.audienceTags,
                    );

                    return (
                      <div key={isUsablePersonaId(persona.personaId) ? persona.personaId : `persona-${idx}`}>
                        <div className="h-px bg-border" />
                        <div className={`flex flex-col sm:flex-row items-start ${isEditPreview ? "gap-3 sm:gap-4 py-3" : "gap-5 sm:gap-8 py-6"}`}>
                          <div className={`flex flex-col sm:flex-row items-start ${isEditPreview ? "gap-3 sm:gap-4" : "gap-4 sm:gap-5"} flex-1 min-w-0`}>
                            <div className={`${isEditPreview ? "w-full sm:w-[142px] sm:max-w-[142px] h-[88px]" : "w-full sm:w-[284px] sm:max-w-[284px] h-44"} sm:shrink-0 rounded-xl bg-surface overflow-hidden flex items-center justify-center`}>
                              {persona.imageUrl ? (
                                <AudienceDetailTwinPhoto imageUrl={persona.imageUrl} />
                              ) : (
                                <Users className="h-7 w-7 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex flex-col gap-1 min-w-0 max-w-[666px]">
                              <p
                                className={isEditPreview ? "font-display font-normal text-[16px] leading-[1.3] text-card-foreground" : "font-display text-card-foreground"}
                                style={isEditPreview ? undefined : fontSizeStyle(fontConfig.persona, titleFontFamily)}
                              >
                                {String(name)}
                              </p>
                              {summary && (
                                <p className={`text-muted-foreground leading-[1.6] line-clamp-3 ${isEditPreview ? "text-[14px]" : "text-base"}`}>
                                  {String(summary)}
                                </p>
                              )}
                              {personaTagNames.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {personaTagNames.map((tn) => (
                                    <span key={tn} className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                                      {tn}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {!isAdmin && twinChatEnabled && isUsablePersonaId(persona.personaId) && (
                            <Button
                              variant="cta"
                              size="sm"
                              className="shrink-0 gap-2"
                              onClick={() => {
                                if (onOpenChatWithTwin && primaryPopulationIdForChat) {
                                  onOpenChatWithTwin({ personaId: persona.personaId, populationId: primaryPopulationIdForChat, personaName: String(name), imageUrl: persona.imageUrl });
                                } else {
                                  navigateToChatWithTwin(persona.personaId, String(name), persona.imageUrl);
                                }
                              }}
                            >
                              <MessageCircle className="h-4 w-4" />
                              {t('audiences.chatNow')}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              <div className="h-px bg-border" />
              {/* Show more / Show less toggle */}
              {(() => {
                const total = featuredTwins.length > 0 ? featuredTwins.length : personas.length;
                const hidden = total - visibleTwinCount;
                const isExpanded = hidden <= 0 && visibleTwinCount > TWINS_PAGE_SIZE;
                if (hidden <= 0 && !isExpanded) return null;
                return (
                  <button
                    type="button"
                    onClick={() => {
                      if (isExpanded) {
                        setVisibleTwinCount(TWINS_PAGE_SIZE);
                      } else {
                        setVisibleTwinCount((c) => c + TWINS_PAGE_SIZE);
                      }
                    }}
                    className="mt-4 self-center text-base font-medium text-foreground hover:text-primary transition-colors"
                  >
                    {isExpanded ? t('audiences.showLess') : t('audiences.showMore')}
                  </button>
                );
              })()}
            </div>
          </div>
        )}

        {/* Bottom CTA — hidden in edit-preview and admin backoffice context */}
        {!isEditPreview && !isAdmin && <div className="relative rounded-2xl p-6 sm:p-10 flex flex-col items-center gap-6 sm:gap-7 overflow-hidden min-h-[200px]">
          {/* Background: same cover image as hero */}
          <div
            className="absolute inset-0"
            style={{ background: 'var(--home-card-placeholder-gradient)' }}
          />
          {displayCoverImage && (
            <FadeImage
              src={displayCoverImage}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: 'center calc(50% + 80px)' }}
            />
          )}
          {/* Gradient overlay: dark on left (image shows through), brand-tinted on right */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, rgb(0 0 0 / 0.1) 0%, color-mix(in srgb, hsl(var(--primary)) 18%, black) 55%, color-mix(in srgb, hsl(var(--primary)) 26%, black) 100%)',
            }}
          />
          <div className="relative z-10 flex flex-col items-center gap-3 text-center px-2">
            <h2 className="font-display text-[24px] sm:text-[32px] leading-[1.2] tracking-[-0.02em] text-white">
              {t('audiences.testIdeasHeading')}
            </h2>
            <p className="text-white text-base sm:text-lg leading-[1.4]">
              {t('audiences.testIdeasSub', { name: displayName ?? '' })}
            </p>
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <Button asChild className="h-11 px-6 text-base font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap justify-center">
              <Link href={`/tests/new?populationId=${audience?.id ?? audienceId}`}>
                <Plus className="mr-2 h-4 w-4" />
                {t('nav.createStudy')}
              </Link>
            </Button>
            {twinChatEnabled && (
            <Button
              className="h-11 px-6 text-base font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap justify-center"
              onClick={() => {
                if (onOpenChat && primaryPopulationIdForChat) {
                  onOpenChat({
                    audienceId: primaryPopulationIdForChat,
                    audience: audience ?? undefined,
                  });
                } else if (isValidPersonaId(firstMeetTheAudiencePersonaId) && primaryPopulationIdForChat) {
                  navigateToChatWithTwin(firstMeetTheAudiencePersonaId);
                } else {
                  navigateTo('/chats?new=1');
                }
              }}
              data-testid="audience-detail-chat-button"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              {t('audiences.chatWithTwins')}
            </Button>
            )}
          </div>
        </div>}
      </div>
    </div>
  );
}

const SHARING_ICON_MAP: Record<string, typeof Lock> = {
  private: Lock,
  shared: Users,
  organization: Building,
  marketplace: Store,
};

function CreatorBadge({ audience, viewerId }: { audience: AudienceDetail | null; viewerId?: string | null }) {
  const { t } = useTranslation('app');
  const sharingMode = audience?.sharingMode || 'private';
  const SharingIcon = SHARING_ICON_MAP[sharingMode] || Lock;

  const isOwnAudience = viewerId && audience?.userId && viewerId === audience.userId;

  const creatorLabel = isOwnAudience
    ? null
    : (audience?.creatorName?.trim() ||
       audience?.creatorEmail?.trim() ||
       audience?.publisherDomain?.trim() ||
       audience?.organizationName?.trim() ||
       null);

  return (
    <div className="flex items-center gap-2 bg-black/40 backdrop-blur-[12px] px-3 py-1.5 rounded-full">
      <SharingIcon className="h-3.5 w-3.5 text-[hsl(var(--platform-card))]" />
      <span className="text-sm font-medium text-white">
        {isOwnAudience
          ? t('audiences.createdByYou')
          : t('audiences.createdByName', { name: creatorLabel ?? t('audiences.unknown') })}
      </span>
    </div>
  );
}
